'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useRef, useState } from 'react';
import { useIntersection } from 'react-use';

import cx from 'classnames';
import PropTypes from 'prop-types';

import Lockup from 'components/blocks/shared/Lockup';
import Img from 'components/ui/Img';

import { blockRootProps } from 'utils';
import { scrollToOffset } from 'utils';

import BubbleButton from './BubbleButton';
import Overlay from './Overlay';

import styles from './BlockHotspot.module.scss';

const BlockHotspot = ({
    __typename,
    eyebrow,
    headline,
    bodyCopy,
    cta,
    labels,
    image,
    imageLarge,
    textColor,
    hotspotButtonColor,
    flushTop,
    flushBottom,
    hotspots,
    layout,
    theme,
    icon,
    anchorId,
}) => {
    textColor ??= 'blue';
    hotspotButtonColor ??= 'blue';
    flushTop ??= true;
    flushBottom ??= true;
    layout ??= 'backgroundImage';

    const isHexButtonColor = hotspotButtonColor.includes('#');

    const rootRef = useRef(null);

    const [isOverlayOpen, toggleOverlayOpen] = useState(false);
    const [currentHotspot, setCurrentHotspot] = useState(0);
    const [isInView, setIsInView] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    const intersection = useIntersection(rootRef, {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
    });

    const handleHotspotClick = e => {
        const index = e.currentTarget.getAttribute('data-index');
        setCurrentHotspot(parseInt(index || '0'));
        toggleOverlayOpen(true);

        scrollToOffset(rootRef?.current, -120, 'smooth');
    };

    useEffect(() => {
        if (intersection?.isIntersecting) {
            setIsInView(true);
        }
    }, [intersection]);

    useEffect(() => {
        // for hotspot button coordinates hydration mismatch
        setIsMounted(true);
    }, []);

    return (
        <section
            ref={rootRef}
            {...blockRootProps(anchorId, __typename)}
            className={cx(styles.root, {
                [styles.isInView]: isInView,
                [styles.flushTop]: flushTop,
                [styles.flushBottom]: flushBottom,
                [styles.halfImageLayout]: layout === 'halfImage',
                [styles[`theme-${theme}`]]: theme,
            })}
            style={{
                '--textColor': `var(--colors-${textColor})`,
            }}
        >
            <div className={styles.inner}>
                <div className={styles.interactive}>
                    {labels && <div className={styles.labels}>{labels}</div>}
                    {image && (
                        <Img
                            className={cx({
                                [styles.backgroundImage]:
                                    layout === 'backgroundImage',
                                [styles.halfImage]: layout === 'halfImage',
                            })}
                            src={image?.url}
                            width={image?.width}
                            height={image?.height}
                            fallbackImageWidth={768}
                            alt={image?.description || image?.title || ''}
                            customSources={[
                                {
                                    breakpoint: 1920,
                                    src: imageLarge?.url,
                                    imageWidth: 1920 * 1.5,
                                },
                                {
                                    breakpoint: 1440,
                                    src: imageLarge?.url,
                                    imageWidth: 1440 * 1.5,
                                },
                                {
                                    breakpoint: 1024,
                                    src: imageLarge?.url,
                                    imageWidth: 1024 * 1.5,
                                },
                                {
                                    breakpoint: 768,
                                    src: imageLarge?.url,
                                    imageWidth: 1024,
                                },
                                {
                                    src: image?.url,
                                    imageWidth: 768,
                                },
                            ]}
                        />
                    )}
                    {isMounted && (
                        <div className={styles.buttons}>
                            {hotspots?.map(
                                (
                                    { headline, position, desktopPosition },
                                    i
                                ) => {
                                    if (position && desktopPosition) {
                                        const coordinates = new RegExp(
                                            /^\[([0-9]{1,2}),([0-9]{1,2})\]$/
                                        );

                                        if (
                                            coordinates.test(position) &&
                                            coordinates.test(desktopPosition)
                                        ) {
                                            const mobileMatch =
                                                coordinates.exec(position);
                                            const desktopMatch =
                                                coordinates.exec(
                                                    desktopPosition
                                                );

                                            if (mobileMatch && desktopMatch) {
                                                const mobile = {
                                                    x: mobileMatch[1],
                                                    y: mobileMatch[2],
                                                };
                                                const desktop = {
                                                    x: desktopMatch[1],
                                                    y: desktopMatch[2],
                                                };

                                                return (
                                                    <div
                                                        key={i}
                                                        className={
                                                            styles.hotspotButton
                                                        }
                                                        style={{
                                                            '--pos-y': `${mobile.y}%`,
                                                            '--pos-x': `${mobile.x}%`,
                                                            '--desktop-pos-y': `${desktop.y}%`,
                                                            '--desktop-pos-x': `${desktop.x}%`,
                                                            '--hotspot-color':
                                                                isHexButtonColor
                                                                    ? hotspotButtonColor
                                                                    : `var(--colors-${hotspotButtonColor})`,
                                                            '--stagger-order':
                                                                i,
                                                            '--pulse-order':
                                                                (i + 1) *
                                                                Math.random(),
                                                        }}
                                                    >
                                                        <BubbleButton
                                                            data-index={i}
                                                            gtm-label={`hotspot - ${headline}`}
                                                            aria-label={`Open details for ${headline}`}
                                                            onClick={
                                                                handleHotspotClick
                                                            }
                                                        />
                                                    </div>
                                                );
                                            }
                                        }
                                    }
                                }
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.contentWrapper}>
                    <div className={styles.contentInner}>
                        <div
                            className={cx(styles.content, {
                                [styles.hide]: isOverlayOpen,
                            })}
                            style={{
                                '--textColor': `var(--colors-${textColor})`,
                            }}
                        >
                            <Lockup
                                icon={icon}
                                eyebrow={{ text: eyebrow }}
                                headline={{ text: headline }}
                                copy={{ text: bodyCopy }}
                                ctas={cta}
                                theme="Large"
                            />
                        </div>
                        {hotspots.map((hotspot, i) => {
                            return (
                                i === currentHotspot && (
                                    <Overlay
                                        key={i}
                                        isOpen={isOverlayOpen}
                                        setIsOpen={toggleOverlayOpen}
                                        isActive={i === currentHotspot}
                                        theme={theme}
                                        {...hotspot}
                                    />
                                )
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

BlockHotspot.propTypes = {
    __typename: PropTypes.string,
    anchorId: PropTypes.string,
    bodyCopy: PropTypes.object,
    copy: PropTypes.string,
    cta: PropTypes.object,
    eyebrow: PropTypes.string,
    flushBottom: PropTypes.bool,
    flushTop: PropTypes.bool,
    headline: PropTypes.string,
    hotspotButtonColor: PropTypes.string,
    hotspots: PropTypes.array,
    icon: PropTypes.shape({
        icon: PropTypes.shape({
            url: PropTypes.any,
        }),
    }),
    image: PropTypes.object,
    imageLarge: PropTypes.object,
    labels: PropTypes.array,
    layout: PropTypes.string,
    preHeadline: PropTypes.string,
    textColor: PropTypes.string,
    theme: PropTypes.string,
};

export default BlockHotspot;
